import { List } from 'immutable'
import _assign from 'lodash/assign'
import _cloneDeep from 'lodash/cloneDeep'
import _get from 'lodash/get'
import _isFunction from 'lodash/isFunction'
import _isArray from 'lodash/isArray'
import _isObject from 'lodash/isObject'
import _toString from 'lodash/toString'
import _isNil from 'lodash/isNil'
import {
  post as apiPost,
  put as apiPut,
  get as apiGet,
  TYPE_BROOKLYN_JSON,
} from '../../api-client/index'
import { get as getConfig } from '../../config/index'
import {
  getVelocityCheckActionLocation,
} from '../velocity-check/index'
import { getPrimary } from '../languages/index'
import { ES_LA, ES_ES } from '../languages/constants'
import { US } from '../country/index'

const config = getConfig()
const adyenPaypal = !!_get(config, ['features', 'checkout', 'adyenPaypal'])
const adyenApplepay = !!_get(config, ['features', 'checkout', 'adyenApplepay'])
const adyenGooglepay = !!_get(config, ['features', 'checkout', 'adyenGooglepay'])
export const adyenPaypalActive = adyenPaypal || (global && _get(global, 'location') && _get(global, ['location', 'pathname']) === '/dev/adyen')
export const adyenApplepayActive = adyenApplepay || (global && _get(global, 'location') && _get(global, ['location', 'pathname']) === '/dev/adyen')
export const adyenGooglepayActive = adyenGooglepay || (global && _get(global, 'location') && _get(global, ['location', 'pathname']) === '/dev/adyen')
export const adyenMerchantAccount = _get(config, 'adyenMerchantAccount')
export const adyenEnvironment = _get(config, 'adyenEnvironment')
export const adyenClientKey = _get(config, 'adyenClientKey')
export const adyenReturnUrl = _get(config, 'adyenReturnUrl')
export const adyenGooglepayEnvironment = _get(config, 'adyenGooglepayEnvironment')
export const ADYEN_PAYMENT_RESPONSE_CODE_AUTHORIZED = 'Authorised'
export const ADYEN_PAYMENT_RESPONSE_CODE_PENDING = 'Pending'
export const ADYEN_PAYMENT_RESPONSE_CODE_RECEIVED = 'Received'
export const ADYEN_FIELD_DEFAULT_VALUE = 'NA'
export const ADYEN_PAYMENT_TYPE_CARD = 'scheme'
export const ADYEN_PAYMENT_TYPE_PAYPAL = 'paypal'
export const ADYEN_PAYMENT_TYPE_APPLEPAY = 'applepay'
export const ADYEN_PAYMENT_TYPE_GOOGLEPAY = 'googlepay'

export const ADYEN_CHANNEL_WEB = 'Web'
export const ADYEN_PLATFORM = 'Gaia'
export const ADYEN_DEFAULT_US_POSTAL_CODE = '80027'
export const ADYEN_DEFAULT_CANADA_POSTAL_CODE = 'M4B 1B3'
// https://docs.adyen.com/online-payments/web-drop-in/customization
export const allowedPaymentMethods = [ADYEN_PAYMENT_TYPE_CARD]
  .concat(adyenPaypalActive ? [ADYEN_PAYMENT_TYPE_PAYPAL] : [])
  .concat(adyenApplepayActive ? [ADYEN_PAYMENT_TYPE_APPLEPAY] : [])
  .concat(adyenGooglepayActive ? [ADYEN_PAYMENT_TYPE_GOOGLEPAY] : [])
export const blockedPaymentMethods = []
  .concat(!adyenApplepayActive ? [ADYEN_PAYMENT_TYPE_APPLEPAY] : [])
  .concat(!adyenGooglepayActive ? [ADYEN_PAYMENT_TYPE_GOOGLEPAY] : [])
export const instantPaymentTypes = []
  .concat(adyenApplepayActive ? [ADYEN_PAYMENT_TYPE_APPLEPAY] : [])
  .concat(adyenGooglepayActive ? [ADYEN_PAYMENT_TYPE_GOOGLEPAY] : [])

const SCHEMA_ADYEN_PAYMENT_METHODS = {
  error: false,
  success: false,
  data: {},
}

const SCHEMA_ADYEN_PAYMENTS = {
  error: false,
  success: false,
  data: {},
}

const SCHEMA_ADYEN_AUTHORIZATION = {
  error: false,
  success: false,
  reference: '',
  uuid: '',
}

// adyen-test-page
export function isTestAdyenPage ({ state }) {
  const { resolver } = state
  const path = resolver.getIn(['data', 'path'])

  return (
    path === '/dev/adyen'
  )
}

export function getBaseNewRelicData (state) {
  const {
    user,
    userAccount,
    auth,
    resolver,
    checkout,
  } = state
  const userUuid = user.getIn(['data', 'uuid'])
  const xff = user.get('xff') || user.getIn(['data', 'xff']) || userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'xff'])
  let newRelicActionLocation = getVelocityCheckActionLocation({ auth, checkout, resolver })
  const userRequestCountry = user.getIn(['data', 'requestCountry'])
  const authCountry = auth.get('country')
  const userAccountCountry = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'countryIso'])
  const country = userAccountCountry || authCountry || userRequestCountry || US
  const language = getPrimary(user.getIn(['data', 'language'], List()))

  // adyen-test-page
  if (isTestAdyenPage({ state })) {
    newRelicActionLocation = 'checkout'
  }

  return {
    userUuid,
    paymentTokenLocation: newRelicActionLocation,
    xff,
    country,
    language,
  }
}

/**
 * @param {Object} options The options for the Adyen config function.
 * @param {Function} options.setAdyenDropInReady Redux action creator
 * @see https://docs.adyen.com/online-payments/web-drop-in/optional-configuration
 * @returns {Object} An optional configuration object used to instantiate the Adyen Drop In
 */
export function adyenOptionalConfiguration (options = {}) {
  const { setAdyenDropInReady } = options
  return {
    showPayButton: adyenPaypalActive || adyenApplepayActive || adyenGooglepayActive,
    ...(adyenPaypalActive || adyenApplepayActive || adyenGooglepayActive)
      && { openFirstPaymentMethod: false },
    instantPaymentTypes,
    onReady: () => {
      if (!_isFunction(setAdyenDropInReady)) {
        return
      }
      setAdyenDropInReady(true)
    },
  }
}

/**
 * @param {Object} options - The options for the Adyen config function.
 * @param {Object} options.paymentMethodsResponse - The response from the Adyen Payment Methods API
 * @param {String} options.locale - The user's locale (language)
 * @param {String} options.country - The user's country
 * @param {String} options.currencyIso - The user's currency ISO
 * @param {Function} options.getAdyenPaymentAuthorization - Redux action creator
 * @param {Function} options.getAdyenPaymentAuthorizationDetails - Redux action creator
 * @param {Function} options.setAdyenDropInError - Redux action creator
 * @param {String} options.submitButtonText - The text for the credit card submit button
 * @param {Boolean} options.isGift - Flag to indicate if the Adyen Drop In is in the gift flow
 * @returns {Object} - A configuration object used to instantiate the Adyen Drop In
 */
export function adyenDropInConfiguration (options) {
  const {
    paymentMethodsResponse,
    locale,
    country,
    currencyIso,
    getAdyenPaymentAuthorization,
    getAdyenPaymentAuthorizationDetails,
    setAdyenDropInError,
    submitButtonText,
    isGift,
  } = options
  // Adyen requires a specific locale for Spanish
  // otherwise it will make a call for a translation file
  // which is blocked by CORS after v6.11.0 upgrade
  const adyenLocale = locale === ES_LA ? ES_ES : locale

  return {
    environment: adyenEnvironment,
    clientKey: adyenClientKey,
    paymentMethodsResponse,
    locale: adyenLocale,
    countryCode: country,
    amount: {
      currency: currencyIso,
      value: 0,
    },
    analytics: {
      enabled: true,
    },
    // https://docs.adyen.com/online-payments/web-components/localization-components#fields
    // https://docs.adyen.com/online-payments/web-components/localization-components#supported-languages
    translations: {
      [adyenLocale]: {
        billingAddress: '',
        payButton: submitButtonText,
        'payButton.saveDetails': submitButtonText,
        confirmPreauthorization: submitButtonText,
      },
    },
    onSubmit: async (state, dropin, actions) => {
      // https://docs.adyen.com/online-payments/web-drop-in/advanced-use-cases/additional-actions
      getAdyenPaymentAuthorization({
        paymentMethod: _get(state, ['data', 'paymentMethod']),
        billingAddress: _get(state, ['data', 'billingAddress']),
        isGift,
        dropin,
        resolve: actions.resolve,
        reject: actions.reject,
      })
    },
    onAdditionalDetails: (state, dropin) => {
      getAdyenPaymentAuthorizationDetails({
        data: _get(state, 'data', {}),
        dropin,
        isGift,
      })
    },
    // https://docs.adyen.com/online-payments/web-drop-in#drop-in-error-handling
    onError: (error, dropin) => {
      const { name, message } = error
      let errorMessage = message

      if (_isArray(message)) {
        errorMessage = _toString(message)
      } else if (_isObject(message)) {
        errorMessage = JSON.stringify(message)
      }

      setAdyenDropInError(`${name}: ${errorMessage}`)
      dropin.setStatus('ready')
    },
  }
}

/**
 * @param {Object} options - The options for the Adyen config function.
 * @param {String} options.cardName - The text for labeling the credit card section
 * @param {String} options.country - The user's country
 * @param {String} options.currencyIso - The user's currency ISO
 * @param {Function} options.setAdyenStateProvinceShown - Redux action creator
 * @param {Function} options.setPaymentBillingInfo - Redux action creator
 * @param {Number|String} options.planCost - Cost of the subscription plan
 * @returns {Object} - A configuration object used to configure the Adyen Drop In payment methods
 */
export function adyenPaymentMethodsConfiguration (options) {
  const {
    cardName,
    country,
    setAdyenStateProvinceShown,
    setAdyenIssuerBin,
    setPaymentBillingInfo,
    currencyIso,
    planCost,
  } = options
  // formatting the plan cost in minor units
  // https://docs.adyen.com/development-resources/currency-codes
  const formattedPlanPrice = parseFloat(planCost) * 100

  return {
    // https://docs.adyen.com/payment-methods/cards/web-drop-in
    card: {
      hasHolderName: false,
      holderNameRequired: false,
      enableStoreDetails: false,
      hideCVC: false,
      name: cardName,
      billingAddressRequired: true,
      data: {
        billingAddress: {
          country,
          street: ADYEN_FIELD_DEFAULT_VALUE,
          city: ADYEN_FIELD_DEFAULT_VALUE,
          stateOrProvince: ADYEN_FIELD_DEFAULT_VALUE,
          houseNumberOrName: ADYEN_FIELD_DEFAULT_VALUE,
        },
      },
      onChange: (state /* dropin */) => {
        const stateOrProvinceValue = _get(state, ['data', 'billingAddress', 'stateOrProvince'])

        if (stateOrProvinceValue && stateOrProvinceValue !== ADYEN_FIELD_DEFAULT_VALUE) {
          setAdyenStateProvinceShown(true)
        }
      },
      onFieldValid: (data) => {
        const issuerBin = _get(data, 'issuerBin')
        if (issuerBin && _isFunction(setAdyenIssuerBin)) {
          setAdyenIssuerBin({ issuerBin })
        }
      },
      // onSubmit: () => {
      //   You can override the parent onSubmit function here
      // },
      // https://docs.adyen.com/payment-methods/cards/custom-card-integration#styling
      styles: {
        base: {
          color: '#44576b',
          fontSmoothing: 'antialiased',
          fontSize: '16px',
          fontFamily: 'Helvetica',
        },
        error: {
          color: '#44576b',
          fontSize: '16px',
        },
        validated: {
          color: '#44576b',
          fontSize: '16px',
        },
      },
    },
    // https://docs.adyen.com/payment-methods/paypal/web-drop-in?tab=_code_payments_code__2
    paypal: {
      blockPayPalCreditButton: true,
      blockPayPalPayLaterButton: true,
    },
    // https://docs.adyen.com/payment-methods/apple-pay/web-drop-in?tab=_code_payments_code__2
    applepay: {
      amount: {
        currency: currencyIso,
        value: formattedPlanPrice,
      },
      countryCode: US,
      requiredBillingContactFields: [
        'postalAddress',
      ],
      onAuthorized: (event, actions) => {
        const { billingAddress: billingInfo } = event
        const postalCode = _get(billingInfo, 'postalCode', '')
        const countryCode = _get(billingInfo, 'country', '')
        let billingAddress = {}

        if (postalCode && countryCode) {
          billingAddress = formatBillingAddress({
            address1: _get(billingInfo, 'street', ''),
            address2: _get(billingInfo, 'houseNumberOrName', ''),
            city: _get(billingInfo, 'city', ''),
            state: _get(billingInfo, 'stateOrProvince', ''),
            postalCode,
            country: countryCode,
          })
        } else {
          billingAddress = formatBillingAddress({
            address1: ADYEN_FIELD_DEFAULT_VALUE,
            address2: ADYEN_FIELD_DEFAULT_VALUE,
            city: ADYEN_FIELD_DEFAULT_VALUE,
            state: ADYEN_FIELD_DEFAULT_VALUE,
            postalCode: ADYEN_DEFAULT_US_POSTAL_CODE,
            country: US,
          })
        }

        setPaymentBillingInfo({
          billingAddress,
          paymentType: ADYEN_PAYMENT_TYPE_APPLEPAY,
        })

        // Resolve the payment flow and close the modal
        actions.resolve()
      },
    },
    // https://docs.adyen.com/payment-methods/google-pay/web-drop-in?tab=_code_payments_code__2
    googlepay: {
      amount: {
        currency: currencyIso,
        value: formattedPlanPrice,
      },
      countryCode: US,
      environment: adyenGooglepayEnvironment,
      billingAddressRequired: true,
      // https://developers.google.com/pay/api/web/reference/request-objects#BillingAddressParameters
      billingAddressParameters: {
        format: 'MIN',
      },
      onAuthorized: (event, actions) => {
        const { billingAddress: billingInfo } = event
        const postalCode = _get(billingInfo, 'postalCode', '')
        const countryCode = _get(billingInfo, 'country', '')
        const billingInfoPresent = postalCode && countryCode

        const billingAddress = formatBillingAddress({
          address1: ADYEN_FIELD_DEFAULT_VALUE,
          address2: ADYEN_FIELD_DEFAULT_VALUE,
          city: ADYEN_FIELD_DEFAULT_VALUE,
          state: ADYEN_FIELD_DEFAULT_VALUE,
          postalCode: billingInfoPresent ? postalCode : ADYEN_DEFAULT_US_POSTAL_CODE,
          country: billingInfoPresent ? countryCode : US,
        })

        setPaymentBillingInfo({
          billingAddress,
          paymentType: ADYEN_PAYMENT_TYPE_GOOGLEPAY,
        })

        actions.resolve()
      },
    },
  }
}

/**
 * @param {Object} options - The options for the function.
 * @param {String} options.address1 - Street address
 * @param {String} options.address2 - House/Appartment number
 * @param {String} options.city - The city
 * @param {String} options.state - The state or province
 * @param {String} options.postalCode - The postal code
 * @param {String} options.country - The country
 * @returns {Object} - An object formatted to match the Adyen billing address object
 */
export function formatBillingAddress (options) {
  const {
    address1,
    address2,
    city,
    state,
    postalCode,
    country,
  } = options

  return {
    street: address1,
    houseNumberOrName: address2,
    postalCode,
    city,
    stateOrProvince: state,
    country,
  }
}

/**
 * @param {Object} res - The response object from the POST Adyen Payment Methods API.
 * @param {Boolean} _dataError - A flag indicating whether an error occurred during the API call.
 * @returns {Object} - An object containing the result of the POST Adyen Payment Methods API call,
 * including whether it was successful, any error that occurred, and the data.
 */
export function handleGetAdyenPaymentMethods (res, _dataError) {
  const data = _get(res, ['body'], {})
  const success = !_dataError

  // Remove storedPaymentMethods from the response
  return _assign(_cloneDeep(SCHEMA_ADYEN_PAYMENT_METHODS), {
    error: _dataError,
    success,
    data: {
      ...data,
      storedPaymentMethods: [],
    },
  })
}

/**
 *
 * @param {Object} options The options for the function
 * @param {Map} options.auth The logged in user's auth
 * @param {String} options.countryCode The logged in user's country
 * @param {String} options.shopperLocale The unique order number (reference)
 * @param {String} options.shopperReference The uuid of the user
 * @param {Object} options.amount The amount of the purchase
 * @param {String} options.amount.currency The currency ISO
 * @param {String} options.amount.value The value of the purchase
 * @returns {Object} The data from the POST Adyen Payment Methods API response
 */
export async function getAdyenPaymentMethods (options = {}) {
  const {
    auth,
    countryCode,
    shopperLocale,
    shopperReference,
    amount,
  } = options
  const data = {
    merchantAccount: adyenMerchantAccount,
    countryCode,
    amount,
    shopperLocale,
    allowedPaymentMethods,
    blockedPaymentMethods,
    channel: ADYEN_CHANNEL_WEB,
    shopperReference: shopperReference || auth.get('uuid'),
  }

  try {
    const res = await apiPost('v1/adyen/paymentMethods', data, { auth }, TYPE_BROOKLYN_JSON)
    return handleGetAdyenPaymentMethods(res)
  } catch (err) {
    return handleGetAdyenPaymentMethods({}, true)
  }
}

/**
 * @param {Object} res - The response object from the POST Adyen Payments API.
 * @param {Boolean} _dataError - A flag indicating whether an error occurred during the API call.
 * @returns {Object} - An object containing the result of the POST Adyen Payments API call,
 * including whether it was successful, any error that occurred, and the data.
 */
export function handleGetAdyenPayments (res, _dataError) {
  const data = _get(res, ['body'], {})
  const errors = _get(data, 'errors') || _dataError
  const success = !errors

  return _assign(_cloneDeep(SCHEMA_ADYEN_PAYMENTS), {
    error: !!errors,
    success,
    data,
  })
}

/**
 *
 * @param {Object} options The options for the function
 * @param {Map} options.auth The logged in user's auth
 * @param {String} options.merchantAccount The name of the Adyen merchant account
 * @param {String} options.reference The unique order number (reference)
 * @param {Object} options.amount The amount of the purchase
 * @param {String} options.amount.currency The currency ISO
 * @param {String} options.amount.value The value of the purchase
 * @param {Object} options.paymentMethod The paymentMethods response
 * from the Adyen paymentMethods API call
 * @param {String} options.shopperReference The uuid of the user
 * @param {String} options.shopperEmail The email of the user
 * @param {String} options.shopperIP The IP address of the user
 * @param {String} options.testResultCode A code that can be entered
 * @param {Object} options.billingAddress The billing address
 * to force a specific response from Adyen for testing purposes
 * @returns {Object} The data from the POST Adyen Payments API response
 */
export async function getAdyenPayments (options = {}) {
  const {
    auth,
    merchantAccount,
    reference,
    amount,
    paymentMethod,
    shopperReference,
    shopperEmail,
    shopperIP,
    testResultCode,
    velocityCheckToken,
    billingAddress,
  } = options
  // We want to send blank '' values to the /payments endpoint instead of 'NA'
  const cleanBillingFields = {
    street: '',
    houseNumberOrName: '',
    city: '',
    stateOrProvince: '',
  }
  // overwrite existing 'NA' values
  const cleanedBillingAddress = {
    ...billingAddress,
    ...cleanBillingFields,
  }

  const data = {
    billingAddress: cleanedBillingAddress,
    merchantAccount,
    amount,
    paymentMethod,
    returnUrl: adyenReturnUrl,
    reference,
    shopperReference: shopperReference || auth.get('uuid'),
    shopperEmail,
    shopperIP,
    storePaymentMethod: true,
    metadata: {
      platform: ADYEN_PLATFORM,
    },
    // set a test result code if one exists
    ...(!_isNil(testResultCode))
      && { additionalData: { RequestedTestAcquirerResponseCode: testResultCode } },
  }

  try {
    const res = await apiPost(`v1/adyen/payments${velocityCheckToken ? `?token=${velocityCheckToken}` : ''}`, data, { auth }, TYPE_BROOKLYN_JSON)
    return handleGetAdyenPayments(res)
  } catch (err) {
    return handleGetAdyenPayments({}, true)
  }
}

/**
 * @param {Object} res - The response object from the POST Adyen Payments Details API.
 * @param {Boolean} _dataError - A flag indicating whether an error occurred during the API call.
 * @returns {Object} - An object containing the result of the POST Adyen Payments Details API call,
 * including whether it was successful, any error that occurred, and the data.
 */
export function handleGetAdyenPaymentDetails (res, _dataError) {
  const data = _get(res, ['body'], {})
  const errors = _get(data, 'errors') || _dataError
  const success = !errors

  return _assign(_cloneDeep(SCHEMA_ADYEN_PAYMENTS), {
    error: !!errors,
    success,
    data,
  })
}

/**
 *
 * @param {Object} options The options for the function
 * @param {Object} options.data The state.data from the onAdditionalDetails callback
 * @param {Map} options.auth The logged in user's auth
 * @returns {Object} The data from the POST Adyen Payments Details API response
 */
export async function getAdyenPaymentDetails (options = {}) {
  const {
    data,
    auth,
  } = options

  try {
    const res = await apiPost('v1/adyen/payments/details', data, { auth }, TYPE_BROOKLYN_JSON)
    return handleGetAdyenPaymentDetails(res)
  } catch (err) {
    return handleGetAdyenPaymentDetails({}, true)
  }
}

/**
 * @param {Object} res - The response object from the POST Adyen Authorization API.
 * @param {Boolean} _dataError - A flag indicating whether an error occurred during the API call.
 * @returns {Object} - An object containing the result of the POST Adyen Authorization API call,
 * including whether it was successful, any error that occurred, and the data.
 */
export function handleGetAdyenAuthorization (res, _dataError) {
  const data = _get(res, ['body'], {})
  const success = !_dataError

  return _assign(_cloneDeep(SCHEMA_ADYEN_AUTHORIZATION), {
    error: _dataError,
    success,
    reference: _get(data, 'merchantReference'),
    uuid: _get(data, 'uuid'),
  })
}

/**
 *
 * @param {Object} options The options for the function
 * @param {Map} options.auth The logged in user's auth
 * @param {String} options.shopperReference The user's uuid
 * @param {String} options.velocityCheckUuid The uuid for the velocity check token
 * @param {String} options.pageName The page name,
 * used to identify what location the Adyen Authorization is being requested from.
 * @returns {Object} The data from the POST Adyen Authorization API response
 */
export async function getAdyenAuthorization (options = {}) {
  const {
    auth,
    shopperReference,
    velocityCheckUuid,
    pageName,
  } = options

  const data = {
    shopperReference: shopperReference || auth.get('uuid'),
    velocityCheckUuid,
    platform: ADYEN_PLATFORM,
    pageName,
  }

  try {
    const res = await apiPost('v1/user/billing/adyen/authorization', data, { auth }, TYPE_BROOKLYN_JSON)
    return handleGetAdyenAuthorization(res)
  } catch (err) {
    return handleGetAdyenAuthorization({}, true)
  }
}

/**
 * @param {Object} res - The response object from the PUT Adyen Authorization API.
 * @param {Boolean} _dataError - A flag indicating whether an error occurred during the API call.
 * @returns {Object} - An object containing the result of the PUT Adyen Authorization API call,
 * including whether it was successful, any error that occurred, and the data.
 */
export function handleUpdateAdyenAuthorization (res, _dataError) {
  const data = _get(res, ['body'], {})
  const success = _get(data, 'success')

  return {
    error: _dataError,
    success,
  }
}

/**
 *
 * @param {Object} options The options for the function
 * @param {Map} options.auth The logged in user's auth
 * @param {String} options.uuid The Adyen Authorization uuid identifier
 * @param {String} options.initialResultCode The initial result code for the Adyen payment
 * @param {String} options.recurrintDetailReference The payment token from Adyen
 * @param {String} options.pspReference The Adyen payment reference
 * @param {Boolean} options.discarded Flag that can be set so the Adyen Auth is set as discarded
 * @param {Boolean} options.billingAccountCreated Flag that can be set to signal
 * a successful transaction
 * @returns {Object} The data from the PUT Adyen Authorization API response
 */
export async function updateAdyenAuthorization (options = {}) {
  const {
    auth,
    uuid,
    initialResultCode,
    recurringDetailReference,
    pspReference,
    discarded,
    billingAccountCreated,
  } = options

  const data = {
    initialResultCode,
    recurringDetailReference,
    pspReference,
    discarded,
    billingAccountCreated,
  }

  try {
    const res = await apiPut(`v1/user/billing/adyen/authorization/${uuid}`, data, { auth }, TYPE_BROOKLYN_JSON)
    return handleUpdateAdyenAuthorization(res)
  } catch (e) {
    return handleUpdateAdyenAuthorization({}, true)
  }
}

/**
 * @param {Object} res - The response object from the PayPal API via Brooklyn.
 * @param {Boolean} _dataError - A flag indicating whether an error occurred during the API call.
 * @returns {Object} - An object containing the result of the PayPal order,
 * including whether it was successful, any error that occurred, and the data of the order.
 */
export function handlePaypalOrder (res, _dataError) {
  const data = _get(res, ['body'], {})
  const success = !_dataError

  return {
    error: _dataError,
    success,
    data,
  }
}

/**
 * @param {Object} options - An object containing the options for the function.
 * @param {String} options.orderId - The id of the PayPal order to retrieve.
 * @param {Object} options.auth - An object containing the authentication information
 * @param {String} options.currency - The currency ISO
 * for the API call.
 * @returns {Object} - The result of the PayPal order, or an empty object if an error occurred.
 */
export async function getPaypalOrder (options = {}) {
  const {
    orderId,
    currency,
    auth,
  } = options

  try {
    const res = await apiGet(`v1/paypal/checkout/orders/${orderId}`, { currency }, { auth }, TYPE_BROOKLYN_JSON)
    return handlePaypalOrder(res)
  } catch (e) {
    return handlePaypalOrder({}, true)
  }
}
